import { IconTrash } from '@tabler/icons-react';
import cn from 'classnames';
import * as React from 'react';

import BaseClickable from '@/components/ui/BaseClickable';
import Loader from '@/components/ui/Loader';
import { clamp } from '@/utils/clamp';

import s from './SwipeToDelete.module.scss';

type SwipeToDeleteProps = {
  className?: string;
  enabled?: boolean;
  actionWidth?: number;
  isLoading: boolean;
  isSuccess: boolean;
  onDelete: () => void;
  children: React.ReactNode;
};

const MIN_SWIPE_DISTANCE = 6;
const DELETE_DISTANCE_MULTIPLIER = 1.5;

const SwipeToDelete: React.FC<SwipeToDeleteProps> = ({
  className,
  enabled = true,
  actionWidth = 64,
  isLoading,
  isSuccess,
  onDelete,
  children,
}) => {
  const [offset, setOffset] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isDismissing, setIsDismissing] = React.useState(false);
  const isBusy = isLoading && isDismissing;

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const widthRef = React.useRef(0);

  const offsetRef = React.useRef(0);
  const startXRef = React.useRef(0);
  const startYRef = React.useRef(0);

  const startOffsetRef = React.useRef(0);
  const draggingRef = React.useRef(false);
  const pointerIdRef = React.useRef<number | null>(null);
  const ignoreClickRef = React.useRef(false);
  const deleteRequestedRef = React.useRef(false);

  const setOffsetValue = React.useCallback((value: number) => {
    offsetRef.current = value;
    setOffset(value);
  }, []);

  const updateWidth = React.useCallback(() => {
    widthRef.current = rootRef.current?.getBoundingClientRect().width ?? 0;
  }, []);

  React.useEffect(() => {
    updateWidth();
  }, [updateWidth]);

  React.useEffect(() => {
    if (!enabled && offsetRef.current !== 0) {
      setOffsetValue(0);
    }
    if (!enabled && isDismissing) {
      setIsDismissing(false);
    }
  }, [isDismissing, enabled, setOffsetValue]);

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled || isDismissing) {
        return;
      }

      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      if ((event.target as HTMLElement | null)?.closest('[data-swipe-action]')) {
        return;
      }

      updateWidth();
      pointerIdRef.current = event.pointerId;
      startXRef.current = event.clientX;
      startYRef.current = event.clientY;
      startOffsetRef.current = offsetRef.current;
      draggingRef.current = false;
      setIsDragging(false);

      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [isDismissing, enabled, updateWidth],
  );

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled || isDismissing || pointerIdRef.current !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - startXRef.current;
      const deltaY = event.clientY - startYRef.current;

      if (!draggingRef.current) {
        if (Math.abs(deltaX) < MIN_SWIPE_DISTANCE || Math.abs(deltaX) < Math.abs(deltaY)) {
          return;
        }

        draggingRef.current = true;
        setIsDragging(true);
      }

      event.preventDefault();
      ignoreClickRef.current = true;

      const maxSwipe = -(widthRef.current || actionWidth);
      const nextOffset = clamp(startOffsetRef.current + deltaX, maxSwipe, 0);

      setOffsetValue(nextOffset);
    },
    [actionWidth, isDismissing, enabled, setOffsetValue],
  );

  const commitOffset = React.useCallback(
    (value: number) => {
      draggingRef.current = false;
      setIsDragging(false);

      requestAnimationFrame(() => {
        setOffsetValue(value);
      });
    },
    [setOffsetValue],
  );

  const startDismiss = React.useCallback(() => {
    if (deleteRequestedRef.current) {
      return;
    }

    deleteRequestedRef.current = true;
    updateWidth();
    setIsDismissing(true);
    const width = widthRef.current || actionWidth;

    commitOffset(-(width + actionWidth));
    onDelete();
  }, [actionWidth, commitOffset, onDelete, updateWidth]);

  const finalizeSwipe = React.useCallback(() => {
    if (!draggingRef.current) {
      return;
    }

    const width = widthRef.current || actionWidth;
    const deleteThreshold = Math.max(actionWidth * DELETE_DISTANCE_MULTIPLIER, width * 0.35);

    if (offsetRef.current <= -deleteThreshold) {
      startDismiss();

      return;
    }

    setIsDismissing(false);
    commitOffset(0);
  }, [actionWidth, commitOffset, startDismiss]);

  const handlePointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerIdRef.current !== event.pointerId) {
        return;
      }

      pointerIdRef.current = null;
      finalizeSwipe();

      if (ignoreClickRef.current) {
        window.setTimeout(() => {
          ignoreClickRef.current = false;
        }, 0);
      }
    },
    [finalizeSwipe],
  );

  const handlePointerCancel = React.useCallback(() => {
    pointerIdRef.current = null;
    finalizeSwipe();

    if (ignoreClickRef.current) {
      window.setTimeout(() => {
        ignoreClickRef.current = false;
      }, 0);
    }
  }, [finalizeSwipe]);

  React.useEffect(() => {
    if (!deleteRequestedRef.current || isLoading) {
      return;
    }

    if (isSuccess) {
      deleteRequestedRef.current = false;

      return;
    }

    deleteRequestedRef.current = false;
    setIsDismissing(false);
    setOffsetValue(0);
  }, [isLoading, isSuccess, setOffsetValue]);

  const handleContentClickCapture = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!enabled) {
        return;
      }

      if (ignoreClickRef.current) {
        ignoreClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();

        return;
      }

      if (offsetRef.current < 0) {
        setOffsetValue(0);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [enabled, setOffsetValue],
  );

  const handleDelete = React.useCallback(() => {
    if (!enabled || isDismissing) {
      return;
    }

    startDismiss();
  }, [isDismissing, enabled, startDismiss]);

  if (!enabled) {
    return <>{children}</>;
  }

  const showActions = isDragging || isDismissing || offset < 0;

  const rootStyle = {
    '--swipe-action-width': `${actionWidth}px`,
    '--swipe-offset': `${offset}px`,
  } as React.CSSProperties;

  return (
    <div
      ref={rootRef}
      className={cn(
        s.root,
        isDragging && s.root_dragging,
        isDismissing && s.root_dismissing,
        showActions && s['root_show-actions'],
        className,
      )}
      style={rootStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <div className={s.root__actions}>
        <BaseClickable type="button" className={s.root__delete} onClick={handleDelete} data-swipe-action>
          {isBusy ? <Loader size="xs" color="white" /> : <IconTrash size={18} />}
        </BaseClickable>
      </div>
      <div className={s.root__content} onClickCapture={handleContentClickCapture}>
        {children}
      </div>
    </div>
  );
};

export default React.memo(SwipeToDelete);
